'use client';

import { useMemo } from 'react';

import { useTranslations } from 'next-intl';

import { P } from '@/components/shared/Typography';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils/cn';
import { formatNumberWithCommasAndDecimals } from '@/lib/utils/formatNumberWithCommasAndDecimals';
import {
  formatTimeAgoFromStart,
  formatTimeAgoString
} from '@/lib/utils/formatTimeAgoString';
import { isPriceEqual } from '@/lib/utils/isPriceEqual';

interface I_MissedPredictionProps {
  predictedPricesDecimal?: string[];
  winningPrices?: number[];
  blockTimestamp?: string;
  resolutionTime?: string;
  startTime?: string;
  onBack?: () => void;
  isOngoing?: boolean;
  decimals?: number;
}

function MissedPrediction({
  predictedPricesDecimal,
  winningPrices,
  blockTimestamp,
  startTime,
  onBack,
  isOngoing = false,
  decimals = 2
}: I_MissedPredictionProps) {
  const t = useTranslations();

  const entryTime = useMemo(() => {
    const blockTimestampISO = blockTimestamp
      ? blockTimestamp.includes('T') || blockTimestamp.includes('Z')
        ? blockTimestamp
        : new Date(parseInt(blockTimestamp, 10) * 1000).toISOString()
      : undefined;

    if (!blockTimestampISO) return '';

    if (startTime) {
      return formatTimeAgoFromStart(startTime, blockTimestampISO);
    }

    return formatTimeAgoString(blockTimestampISO);
  }, [blockTimestamp, startTime]);

  const formattedData = useMemo(() => {
    if (!predictedPricesDecimal || predictedPricesDecimal.length === 0) {
      return [];
    }

    let pricesToDisplay = predictedPricesDecimal;
    if (winningPrices && winningPrices.length > 0) {
      pricesToDisplay = predictedPricesDecimal.filter(priceStr => {
        // Exclude prices that match a winning price
        // Use decimal-aware comparison (2060.19 !== 2060.20)
        const compareDecimals = Math.max(decimals, 5);
        return !winningPrices.some(winningPrice =>
          isPriceEqual(priceStr, winningPrice, compareDecimals)
        );
      });
    }

    return pricesToDisplay.map(price => ({
      prediction: formatNumberWithCommasAndDecimals(price, decimals),
      missed: '- 1',
      entry: entryTime
    }));
  }, [predictedPricesDecimal, winningPrices, entryTime, decimals]);

  if (!formattedData.length) {
    return (
      <div className="flex justify-center items-center py-8">
        <P className="text-muted-foreground">
          {t('profile.predictionWinners.noWinnersFound')}
        </P>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-10 outline-1 outline-base-border/20 overflow-hidden flex flex-col max-h-100">
        <div className="flex sticky top-0 z-10">
          <div className="flex-1 px-2 py-2.5 border-b border-base-border/20 flex items-center">
            <P className="flex-1 justify-center text-center text-muted-foreground text-sm font-bold leading-5">
              {t('profile.predictionWinners.prediction')}
            </P>
          </div>

          {!isOngoing && (
            <div className="flex-1 px-2 py-2.5 border-b border-base-border/20 flex items-center">
              <P className="flex-1 justify-center text-center text-muted-foreground text-sm font-bold leading-5">
                {t('profile.predictionWinners.missed')}
              </P>
            </div>
          )}

          <div className="flex-1 px-2 py-2.5 border-b border-base-border/20 flex items-center">
            <P className="flex-1 justify-center text-center text-muted-foreground text-sm font-bold leading-5">
              {t('profile.predictionWinners.entry')}
            </P>
          </div>
        </div>

        <div className="flex h-40 overflow-y-auto flex-1">
          <div className="flex-1 flex flex-col">
            {formattedData.map((item, index) => (
              <div
                key={index}
                className={cn(
                  'min-w-20 px-2 py-2.5 flex justify-center',
                  index === 0 && 'rounded-tl-10',
                  index === formattedData.length - 1 && 'rounded-bl-10',
                  isOngoing &&
                    index === formattedData.length - 1 &&
                    'rounded-br-10',
                  index !== formattedData.length - 1 &&
                    'border-b border-base-border/20'
                )}
              >
                <div className="text-center">
                  <P className="text-foreground text-sm font-medium leading-5">
                    {item.prediction || '-'}
                  </P>
                </div>
              </div>
            ))}
          </div>

          {!isOngoing && (
            <div className="flex-1 flex flex-col">
              {formattedData.map((item, index) => (
                <div
                  key={index}
                  className={cn(
                    'min-w-20 px-2 py-2.5 flex justify-center',
                    index !== formattedData.length - 1 &&
                      'border-b border-base-border/20'
                  )}
                >
                  <div className="text-center">
                    <P className="text-foreground text-sm font-normal leading-5">
                      {item.missed || '-'}
                    </P>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex-1 flex flex-col">
            {formattedData.map((item, index) => (
              <div
                key={index}
                className={cn(
                  'min-w-20 px-2 py-2.5 flex justify-center',
                  index === 0 &&
                    (isOngoing ? 'rounded-tr-10' : 'rounded-tr-10'),
                  index === formattedData.length - 1 && 'rounded-br-10',
                  index !== formattedData.length - 1 &&
                    'border-b border-base-border/20'
                )}
              >
                <div className="text-center">
                  <P className="text-foreground text-sm font-normal leading-5">
                    {item.entry || '-'}
                  </P>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {onBack && (
        <div className="pt-6">
          <Button
            variant="outline"
            size="lg"
            className="w-full uppercase"
            onClick={onBack}
          >
            {t('profile.predictionWinners.backToSubmissions')}
          </Button>
        </div>
      )}
    </>
  );
}

export default MissedPrediction;
